import { useState, useEffect, useCallback } from "react";
import { FileText, Plus, Trash2, Loader2, Users, Dumbbell, ClipboardList, ListChecks, LogOut, UserRound, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ExerciseLibrary from "@/components/admin/ExerciseLibrary";
import ProgramBuilder from "@/components/admin/ProgramBuilder";
import QuestionnairesList from "@/components/admin/QuestionnairesList";
import PatientsManager from "@/components/admin/PatientsManager";
import LetterForm from "@/components/admin/LetterForm";
import { useAuth, formatApiError } from "@/auth/AuthContext";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { id: "patients", label: "Pazienti", icon: UserRound },
  { id: "letters", label: "Lettere", icon: FileText },
  { id: "subscribers", label: "Abbonati", icon: Users },
  { id: "exercises", label: "Esercizi", icon: Dumbbell },
  { id: "programs", label: "Schede", icon: ListChecks },
  { id: "questionnaires", label: "Questionari", icon: ClipboardList },
];

const AdminLogin = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await onLogin(email, password);
      if (user.role !== "admin") {
        toast.error("Questo account non è amministratore");
      } else {
        toast.success("Accesso effettuato");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto">
      <div className="bg-white rounded-3xl p-8 shadow-[0_8px_30px_rgba(0,0,0,0.06)] border border-stone-100">
        <h1 className="font-serif text-2xl text-stone-900 mb-6 text-center">Area Admin</h1>
        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            required
            className="w-full px-4 py-3 rounded-xl border border-stone-200 focus:outline-none focus:ring-2 focus:ring-[#2c4c3b]/20"
            data-testid="admin-email-input"
          />
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            required
            className="w-full px-4 py-3 rounded-xl border border-stone-200 focus:outline-none focus:ring-2 focus:ring-[#2c4c3b]/20"
            data-testid="admin-password-input"
          />
          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-[#2c4c3b] hover:bg-[#1a2e22] text-stone-50 rounded-full py-6"
            data-testid="admin-login-btn"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Accedi"}
          </Button>
        </form>
      </div>
    </div>
  );
};

const AdminPage = () => {
  const { user, login, logout } = useAuth();
  const [letters, setLetters] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [exercises, setExercises] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [questionnaires, setQuestionnaires] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showNewLetter, setShowNewLetter] = useState(false);
  const [activeTab, setActiveTab] = useState("patients");

  const isAdmin = user && user.role === "admin";

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const config = { withCredentials: true };
      const [lettersRes, subsRes, exRes, progRes, questRes] = await Promise.all([
        axios.get(`${API}/letters`, config),
        axios.get(`${API}/subscribers`, config),
        axios.get(`${API}/exercises`, config),
        axios.get(`${API}/programs`, config),
        axios.get(`${API}/questionnaires`, config),
      ]);
      setLetters(lettersRes.data);
      setSubscribers(subsRes.data || []);
      setExercises(exRes.data || []);
      setPrograms(progRes.data || []);
      setQuestionnaires(questRes.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) fetchData();
  }, [isAdmin, fetchData]);

  const handleDeleteLetter = async (letterId) => {
    if (!window.confirm("Sei sicura di voler eliminare questa lettera?")) return;
    try {
      await axios.delete(`${API}/letters/${letterId}`, { withCredentials: true });
      toast.success("Lettera eliminata");
      fetchData();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const handleSendLetter = async (letterId) => {
    try {
      await axios.post(`${API}/letters/${letterId}/send`, {}, { withCredentials: true });
      toast.success("Lettera inviata via email");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'invio");
    }
  };

  if (user === null) {
    return (
      <div className="min-h-screen bg-stone-50" data-testid="admin-page">
        <Header />
        <div className="pt-48 flex justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#2c4c3b]" />
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-stone-50" data-testid="admin-page">
        <Header />
        <main className="pt-40 sm:pt-60 pb-24 px-6">
          <AdminLogin onLogin={login} />
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50" data-testid="admin-page">
      <Header />
      <main className="pt-40 sm:pt-60 pb-24 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="font-serif text-3xl text-stone-900">Dashboard Admin</h1>
              <p className="text-stone-500 text-sm mt-1">Gestisci lettere, esercizi, schede e abbonati</p>
            </div>
            <div className="flex gap-2">
              {activeTab === "letters" && (
                <Button onClick={() => setShowNewLetter(true)} className="bg-[#2c4c3b] hover:bg-[#1a2e22] text-stone-50 rounded-full" data-testid="new-letter-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Nuova Lettera
                </Button>
              )}
              <Button variant="outline" onClick={logout} className="rounded-full border-stone-300 text-stone-600" data-testid="admin-logout-btn">
                <LogOut className="w-4 h-4 mr-1.5" />
                Esci
              </Button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-8 overflow-x-auto pb-1">
            {TABS.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setActiveTab(id)}
                className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all whitespace-nowrap flex items-center ${
                  activeTab === id ? "bg-[#2c4c3b] text-stone-50" : "bg-white text-stone-600 hover:bg-stone-100"
                }`}
                data-testid={`admin-tab-${id}`}
              >
                <Icon className="w-4 h-4 mr-2" />
                {label}
              </button>
            ))}
          </div>

          {showNewLetter && (
            <LetterForm
              onClose={() => setShowNewLetter(false)}
              onSaved={() => {
                setShowNewLetter(false);
                fetchData();
              }}
            />
          )}

          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#2c4c3b]" />
            </div>
          ) : (
            <>
              {activeTab === "letters" && (
                <div className="space-y-4">
                  {letters.length === 0 ? (
                    <div className="text-center py-12 bg-white rounded-2xl border border-stone-100">
                      <FileText className="w-12 h-12 text-stone-200 mx-auto mb-4" />
                      <p className="text-stone-500">Nessuna lettera creata</p>
                    </div>
                  ) : (
                    letters.map((letter) => (
                      <div key={letter.id} className="bg-white rounded-2xl p-6 border border-stone-100 shadow-[0_4px_20px_rgba(0,0,0,0.04)]">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex items-start gap-4">
                            <div className="w-12 h-12 rounded-xl bg-[#e8ece6] flex items-center justify-center flex-shrink-0">
                              <FileText className="w-5 h-5 text-[#2c4c3b]" />
                            </div>
                            <div>
                              <h3 className="font-serif text-lg text-stone-900">{letter.title}</h3>
                              <p className="text-sm text-[#8a9a86]">{letter.month}</p>
                              <p className="text-xs text-stone-400 mt-2">
                                Per: {letter.patient_name} ({letter.patient_email})
                              </p>
                              {letter.last_sent_at && (
                                <p className="text-xs text-[#8a9a86] mt-1">
                                  ✉ Inviata il {new Date(letter.last_sent_at).toLocaleDateString("it-IT", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-1 flex-shrink-0">
                            <button
                              onClick={() => handleSendLetter(letter.id)}
                              title={letter.last_sent_at ? "Reinvia via email" : "Invia via email"}
                              className="p-2 text-stone-400 hover:text-[#2c4c3b] hover:bg-[#e8ece6] rounded-lg transition-colors"
                              data-testid={`send-letter-btn-${letter.id}`}
                            >
                              <Send className="w-5 h-5" />
                            </button>
                            <button onClick={() => handleDeleteLetter(letter.id)} className="p-2 text-stone-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors">
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {activeTab === "subscribers" && (
                <div className="space-y-4">
                  {subscribers.length === 0 ? (
                    <div className="text-center py-12 bg-white rounded-2xl border border-stone-100">
                      <Users className="w-12 h-12 text-stone-200 mx-auto mb-4" />
                      <p className="text-stone-500">Nessun abbonato</p>
                    </div>
                  ) : (
                    subscribers.map((sub) => (
                      <div key={sub.id} className="bg-white rounded-2xl p-6 border border-stone-100 shadow-[0_4px_20px_rgba(0,0,0,0.04)]">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="font-medium text-stone-900">{sub.name}</h3>
                            <p className="text-sm text-stone-500">{sub.email}</p>
                            {sub.preferred_name && (
                              <p className="text-xs text-[#8a9a86] mt-0.5">Preferisce: {sub.preferred_name}</p>
                            )}
                          </div>
                          <div className="text-right">
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                              sub.status === "active" ? "bg-green-100 text-green-700" : "bg-stone-100 text-stone-500"
                            }`}>
                              {sub.status === "active" ? "Attivo" : sub.status}
                            </span>
                            <p className="text-xs text-stone-400 mt-1">Piano: {sub.plan_id}</p>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {activeTab === "patients" && (
                <PatientsManager exercises={exercises} subscribers={subscribers} onRefresh={fetchData} />
              )}
              {activeTab === "exercises" && <ExerciseLibrary exercises={exercises} onRefresh={fetchData} />}
              {activeTab === "programs" && (
                <ProgramBuilder programs={programs} exercises={exercises} subscribers={subscribers} onRefresh={fetchData} />
              )}
              {activeTab === "questionnaires" && <QuestionnairesList questionnaires={questionnaires} />}
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default AdminPage;
